// Pure text matching for "Find in this parasha" (Tranche 4). DOM-free,
// network-free, deterministic.
//
// Hebrew-aware: pesukim carry nikud + te'amim; learners type bare letters.
// Matching runs over a NORMALIZED projection (combining marks dropped, dashes/
// maqaf as spaces, lowercased) while keeping a per-character index map back to
// the ORIGINAL string, so highlights land on the exact original characters -
// the text itself is never modified or re-rendered.

export interface TextMatch {
  start: number; // inclusive, in the ORIGINAL string
  end: number; // exclusive, in the ORIGINAL string
}

// nikud, te'amim, geresh/gershayim + ASCII quotes used as geresh (same ranges
// citations.ts strips - kept in sync by the tests). \u escapes, never raw
// marks, so cp1252 tooling and formatters can't mangle them.
const SKIP = new RegExp("[\\u0591-\\u05C7\\u05F3\\u05F4'\"]");
// maqaf + en/em dash + hyphen read as a word break.
const DASH = new RegExp("[\\u05BE\\u2013\\u2014-]");

function normalizeWithMap(s: string): { norm: string; map: number[] } {
  const chars: string[] = [];
  const map: number[] = [];
  for (let i = 0; i < s.length; i++) {
    const ch = s[i];
    // DASH before SKIP: maqaf (U+05BE) sits inside the SKIP range but must
    // become a word break, not vanish.
    if (DASH.test(ch) || /\s/.test(ch)) {
      chars.push(" ");
      map.push(i);
      continue;
    }
    if (SKIP.test(ch)) continue;
    chars.push(ch.toLowerCase());
    map.push(i);
  }
  return { norm: chars.join(""), map };
}

// Normalize a learner's query the same way (plus collapse internal runs of
// whitespace, since queries are typed, not quoted).
export function normalizeQuery(q: string): string {
  return normalizeWithMap(q).norm.replace(/\s+/g, " ").trim();
}

export const MIN_QUERY = 2; // normalized characters; below this, no search

// All matches of `query` in `original`, as original-string ranges.
export function findMatches(original: string, query: string): TextMatch[] {
  const q = normalizeQuery(query);
  if (q.length < MIN_QUERY) return [];
  const { norm, map } = normalizeWithMap(original);
  const out: TextMatch[] = [];
  let from = 0;
  for (;;) {
    const i = norm.indexOf(q, from);
    if (i === -1) break;
    out.push({ start: map[i], end: map[i + q.length - 1] + 1 });
    from = i + q.length;
  }
  return out;
}
